import { default as agriculture } from "../../assets/agri.jpg";
import { default as distribution } from "../../assets/supply.jpg";
import { default as ecommerce } from "../../assets/ecommerce.jpg";
import { default as service } from "../../assets/service.jpg";
import { default as rural } from "../../assets/rural.jpg";
import { default as manufacturing } from "../../assets/manufacturing.jpg";
import { default as development } from "../../assets/development.png";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Grid, Typography, useMediaQuery } from "@mui/material";
import { theme } from "../../common/style/Theme";

const Industries = () => {
    const { id } = useParams();

    const [industryIndex, setIndustryIndex] = useState(0);
    const [industries, setIndustries] = useState({});
    
    // Responsive breakpoints
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        switch (id) {
            case "manufacturing":
                setIndustryIndex(0);
                setIndustries({
                    image: manufacturing,
                    name: "Manufacturing & Processing",
                    description: "The manufacturing and processing industry is the backbone of the economy, producing goods that are essential for daily life. M5 Info Solutions provides innovative software solutions to streamline operations, enhance productivity, and ensure quality control in manufacturing processes.",
                    keyChallenges: [
                        {
                            name: "Manual and Paper-based Processes",
                        },
                        {
                            name: "Inefficient Supply Chain Management",
                        },
                        {
                            name: "Poor Data Management",
                        },
                        {
                            name: "Lack of real-time data visibility"
                        },
                        {
                            name: "Difficulty in managing inventory and resources"
                        },
                        {
                            name: "Compliance with industry regulations"
                        }
                    ],
                    solutions: [
                        {
                            name: "Custom ERP for Manufacturing",
                            description: "A custom ERP solution designed specifically for the manufacturing industry, integrating all aspects of production, supply chain, and inventory management.",
                            deliverables: [
                                "Streamlined production processes",
                                "Real-time inventory tracking",
                                "Automated supply chain management",
                                "Enhanced data analytics and reporting"
                            ]
                        },
                        {
                            name: "IoT Integration for Smart Manufacturing",
                            description: "Integration of IoT devices to monitor and control manufacturing processes in real-time, improving efficiency and reducing downtime.",
                            deliverables: [
                                "Real-time monitoring of equipment",
                                "Predictive maintenance alerts",
                                "Data-driven decision making",
                                "Improved operational efficiency"
                            ]
                        },
                        {
                            name: "Quality Control Systems",
                            description: "Automated quality control systems to ensure products meet industry standards and reduce defects.",
                            deliverables: [
                                "Automated quality checks",
                                "Data analytics for quality improvement",
                                "Compliance with industry standards",
                                "Reduced defect rates"
                            ]
                        },
                        {
                            name: "Supply Chain Optimization",
                            description: "Solutions to optimize supply chain management, from procurement to distribution, ensuring timely delivery and cost efficiency.",
                            deliverables: [
                                "Automated procurement processes",
                                "Real-time supply chain visibility",
                                "Cost reduction strategies",
                                "Improved supplier management"
                            ]
                        },
                        {
                            name: "Data Analytics and Reporting",
                            description: "Advanced data analytics tools to provide insights into manufacturing processes, helping businesses make informed decisions.",
                            deliverables: [
                                "Custom dashboards for real-time insights",
                                "Automated reporting",
                                "Data visualization tools",
                                "Enhanced decision-making capabilities"
                            ]
                        },
                        {
                            name: "Training and Support",
                            description: "Comprehensive training and support services to ensure successful implementation and adoption of software solutions.",
                            deliverables: [
                                "On-site and remote training sessions",
                                "24/7 technical support",
                                "User manuals and documentation",
                                "Ongoing software updates and maintenance"
                            ]
                        }
                    ],
                    process: [
                        {
                            name: "Industry Analysis and Discovery",
                            percent: 20,
                            color: theme.palette.background.default
                        },
                        {
                            name: "Solution Design and Prototyping",
                            percent: 50,
                            color: theme.palette.secondary.main
                        },
                        {
                            name: "Development and Implementation",
                            percent: 70,
                            color: theme.palette.warning.main
                        },
                        {
                            name: "Testing and Quality Assurance",
                            percent: 50,
                            color: theme.palette.warning.light
                        },
                        {
                            name: "Deployment and Support",
                            percent: 30,
                            color: theme.palette.background.default
                        }
                    ]
                });
                break;
            case "distribution":
                setIndustryIndex(1);
                setIndustries({
                    image: distribution,
                    name: "Distribution & Supply Chain",
                    description: "Transform your distribution network with intelligent supply chain solutions that provide end-to-end visibility, optimize logistics, and enable seamless coordination from warehouses to last-mile delivery.",
                    keyChallenges: [
                        {
                            name: "Inventory Management Complexity",
                        },
                        {
                            name: "Multi-location Coordination",
                        },
                        {
                            name: "Last-mile Delivery Optimization",
                        },
                        {
                            name: "Demand Forecasting Accuracy"
                        },
                        {
                            name: "Supplier Relationship Management"
                        },
                        {
                            name: "Cost Control and Margin Optimization"
                        }
                    ],
                    solutions: [
                        {
                            name: "Distribution Management System",
                            description: "Comprehensive system to manage multi-location inventory, order processing, and distribution logistics with real-time tracking and optimization.",
                            deliverables: [
                                "Multi-warehouse inventory management",
                                "Automated order routing and allocation",
                                "Real-time shipment tracking",
                                "Performance analytics and KPIs"
                            ]
                        },
                        {
                            name: "Supply Chain Visibility Platform",
                            description: "End-to-end visibility platform that tracks products from suppliers to end customers, providing insights for optimization.",
                            deliverables: [
                                "Real-time supply chain monitoring",
                                "Predictive analytics for demand planning",
                                "Supplier performance dashboards",
                                "Risk management and alerts"
                            ]
                        },
                        {
                            name: "Logistics Optimization Engine",
                            description: "AI-powered logistics optimization for route planning, load optimization, and delivery scheduling to reduce costs and improve efficiency.",
                            deliverables: [
                                "Dynamic route optimization",
                                "Load planning and consolidation",
                                "Delivery scheduling automation",
                                "Cost reduction analytics"
                            ]
                        },
                        {
                            name: "Warehouse Management System",
                            description: "Advanced WMS with barcode/RFID integration, automated picking, and inventory optimization for maximum warehouse efficiency.",
                            deliverables: [
                                "Automated inventory tracking",
                                "Optimized picking and packing",
                                "Real-time stock level monitoring",
                                "Warehouse performance analytics"
                            ]
                        },
                        {
                            name: "Vendor Management Portal",
                            description: "Digital platform for managing supplier relationships, purchase orders, and vendor performance with automated workflows.",
                            deliverables: [
                                "Vendor onboarding and management",
                                "Automated purchase order processing",
                                "Supplier performance tracking",
                                "Contract and compliance management"
                            ]
                        },
                        {
                            name: "Customer Portal Integration",
                            description: "Customer-facing portal for order tracking, delivery updates, and self-service capabilities to enhance customer experience.",
                            deliverables: [
                                "Real-time order tracking",
                                "Delivery notification system",
                                "Customer self-service options",
                                "Feedback and rating system"
                            ]
                        }
                    ],
                    process: [
                        {
                            name: "Supply Chain Assessment",
                            percent: 20,
                            color: theme.palette.background.default
                        },
                        {
                            name: "Process Mapping and Optimization",
                            percent: 50,
                            color: theme.palette.secondary.main
                        },
                        {
                            name: "System Integration and Development",
                            percent: 70,
                            color: theme.palette.warning.main
                        },
                        {
                            name: "Testing and Performance Validation",
                            percent: 50,
                            color: theme.palette.warning.light
                        },
                        {
                            name: "Rollout and Continuous Optimization",
                            percent: 30,
                            color: theme.palette.background.default
                        }
                    ]
                });
                break;
            case "agriculture":
                setIndustryIndex(2);
                setIndustries({
                    image: agriculture,
                    name: "Agriculture & Allied Sectors",
                    description: "Empower farmers and agribusinesses with digital solutions that connect the entire agricultural ecosystem - from farm to fork. Our platforms enable data-driven farming, market access, and value chain optimization.",
                    keyChallenges: [
                        {
                            name: "Limited Market Access for Farmers",
                        },
                        {
                            name: "Lack of Digital Literacy",
                        },
                        {
                            name: "Price Volatility and Transparency",
                        },
                        {
                            name: "Quality Assessment and Grading"
                        },
                        {
                            name: "Supply Chain Inefficiencies"
                        },
                        {
                            name: "Financial Inclusion and Credit Access"
                        }
                    ],
                    solutions: [
                        {
                            name: "Farmer-to-Market Platform",
                            description: "Digital marketplace connecting farmers directly with buyers, eliminating intermediaries and ensuring fair pricing for agricultural produce.",
                            deliverables: [
                                "Direct farmer-buyer connectivity",
                                "Real-time price discovery",
                                "Quality assessment tools",
                                "Payment gateway integration"
                            ]
                        },
                        {
                            name: "Precision Agriculture System",
                            description: "IoT and satellite-based monitoring system for crop health, soil conditions, and weather patterns to optimize farming practices.",
                            deliverables: [
                                "Crop monitoring and alerts",
                                "Soil health analysis",
                                "Weather-based recommendations",
                                "Yield prediction models"
                            ]
                        },
                        {
                            name: "Agri-Supply Chain Management",
                            description: "End-to-end supply chain solution for agricultural products with cold chain monitoring, logistics optimization, and traceability.",
                            deliverables: [
                                "Cold chain monitoring",
                                "Logistics route optimization",
                                "Product traceability system",
                                "Quality control checkpoints"
                            ]
                        },
                        {
                            name: "Agricultural Finance Platform",
                            description: "Digital lending and insurance platform specifically designed for farmers with crop-based risk assessment and flexible repayment options.",
                            deliverables: [
                                "Crop-based loan products",
                                "Risk assessment algorithms",
                                "Insurance claim processing",
                                "Financial literacy modules"
                            ]
                        },
                        {
                            name: "Agri-Input Management System",
                            description: "Platform for managing agricultural inputs like seeds, fertilizers, and pesticides with inventory tracking and quality assurance.",
                            deliverables: [
                                "Input inventory management",
                                "Quality certification tracking",
                                "Seasonal demand planning",
                                "Supplier network management"
                            ]
                        },
                        {
                            name: "Knowledge Sharing Platform",
                            description: "Digital extension services platform providing farmers with best practices, weather updates, and expert advice through mobile applications.",
                            deliverables: [
                                "Expert advisory services",
                                "Weather and market updates",
                                "Best practices database",
                                "Peer-to-peer learning network"
                            ]
                        }
                    ],
                    process: [
                        {
                            name: "Agricultural Ecosystem Analysis",
                            percent: 20,
                            color: theme.palette.background.default
                        },
                        {
                            name: "Stakeholder Engagement and Design",
                            percent: 50,
                            color: theme.palette.secondary.main
                        },
                        {
                            name: "Platform Development and Testing",
                            percent: 70,
                            color: theme.palette.warning.main
                        },
                        {
                            name: "Pilot Implementation and Validation",
                            percent: 50,
                            color: theme.palette.warning.light
                        },
                        {
                            name: "Scale-up and Continuous Support",
                            percent: 30,
                            color: theme.palette.background.default
                        }
                    ]
                });
                break;
            case "retail":
                setIndustryIndex(3);
                setIndustries({
                    image: ecommerce,
                    name: "Retail & eCommerce",
                    description: "Transform your retail operations with omnichannel solutions that seamlessly integrate online and offline experiences. From inventory management to customer engagement, we create platforms that drive sales and customer loyalty.",
                    keyChallenges: [
                        {
                            name: "Omnichannel Integration Complexity",
                        },
                        {
                            name: "Inventory Synchronization Issues",
                        },
                        {
                            name: "Customer Experience Consistency",
                        },
                        {
                            name: "Payment Gateway Integration"
                        },
                        {
                            name: "Competition from Online Giants"
                        },
                        {
                            name: "Data-driven Personalization"
                        }
                    ],
                    solutions: [
                        {
                            name: "Omnichannel Retail Platform",
                            description: "Unified platform that integrates online and offline retail operations with consistent customer experience across all touchpoints.",
                            deliverables: [
                                "Unified customer profiles",
                                "Cross-channel inventory visibility",
                                "Consistent pricing and promotions",
                                "Seamless order fulfillment"
                            ]
                        },
                        {
                            name: "eCommerce Marketplace Solution",
                            description: "Comprehensive eCommerce platform with multi-vendor support, advanced search, and personalized recommendations.",
                            deliverables: [
                                "Multi-vendor marketplace",
                                "Advanced product search and filtering",
                                "Personalized recommendations",
                                "Mobile-responsive design"
                            ]
                        },
                        {
                            name: "Point of Sale (POS) Integration",
                            description: "Modern POS system integrated with inventory management, customer loyalty programs, and analytics for enhanced in-store experience.",
                            deliverables: [
                                "Integrated POS system",
                                "Real-time inventory updates",
                                "Customer loyalty integration",
                                "Sales analytics and reporting"
                            ]
                        },
                        {
                            name: "Customer Data Platform",
                            description: "Unified customer data platform that aggregates customer interactions across all channels for personalized marketing and service.",
                            deliverables: [
                                "360-degree customer view",
                                "Behavioral analytics",
                                "Personalized marketing campaigns",
                                "Customer journey mapping"
                            ]
                        },
                        {
                            name: "Supply Chain Optimization",
                            description: "Retail-specific supply chain solution with demand forecasting, automated reordering, and vendor management capabilities.",
                            deliverables: [
                                "Demand forecasting algorithms",
                                "Automated inventory replenishment",
                                "Vendor performance management",
                                "Cost optimization analytics"
                            ]
                        },
                        {
                            name: "Digital Marketing Suite",
                            description: "Integrated digital marketing platform with email campaigns, social media management, and customer engagement tools.",
                            deliverables: [
                                "Email marketing automation",
                                "Social media integration",
                                "Customer engagement tools",
                                "Marketing ROI analytics"
                            ]
                        }
                    ],
                    process: [
                        {
                            name: "Retail Process Assessment",
                            percent: 20,
                            color: theme.palette.background.default
                        },
                        {
                            name: "Customer Journey Mapping",
                            percent: 50,
                            color: theme.palette.secondary.main
                        },
                        {
                            name: "Platform Development and Integration",
                            percent: 70,
                            color: theme.palette.warning.main
                        },
                        {
                            name: "User Experience Testing",
                            percent: 50,
                            color: theme.palette.warning.light
                        },
                        {
                            name: "Launch and Performance Optimization",
                            percent: 30,
                            color: theme.palette.background.default
                        }
                    ]
                });
                break;
            case "service-based":
                setIndustryIndex(4);
                setIndustries({
                    image: service,
                    name: "Service-based Enterprises",
                    description: "Digitize service delivery models with platforms that streamline operations, enhance customer experiences, and scale service businesses efficiently. From consulting to healthcare, we create solutions that transform service delivery.",
                    keyChallenges: [
                        {
                            name: "Service Standardization Issues",
                        },
                        {
                            name: "Resource Allocation and Scheduling",
                        },
                        {
                            name: "Quality Assurance and Monitoring",
                        },
                        {
                            name: "Customer Communication Management"
                        },
                        {
                            name: "Performance Measurement Challenges"
                        },
                        {
                            name: "Scalability and Growth Constraints"
                        }
                    ],
                    solutions: [
                        {
                            name: "Service Management Platform",
                            description: "Comprehensive platform for managing service delivery lifecycle from booking to completion with automated workflows and customer communication.",
                            deliverables: [
                                "Service booking and scheduling",
                                "Automated workflow management",
                                "Customer communication tools",
                                "Service quality tracking"
                            ]
                        },
                        {
                            name: "Resource Optimization System",
                            description: "AI-powered resource allocation system that optimizes staff scheduling, workload distribution, and capacity planning for maximum efficiency.",
                            deliverables: [
                                "Automated staff scheduling",
                                "Workload optimization",
                                "Capacity planning tools",
                                "Resource utilization analytics"
                            ]
                        },
                        {
                            name: "Customer Portal and Mobile App",
                            description: "Self-service customer portal and mobile application for service requests, tracking, feedback, and account management.",
                            deliverables: [
                                "Self-service booking system",
                                "Real-time service tracking",
                                "Customer feedback platform",
                                "Account management features"
                            ]
                        },
                        {
                            name: "Quality Management System",
                            description: "Digital quality assurance platform with standardized checklists, performance monitoring, and continuous improvement tools.",
                            deliverables: [
                                "Standardized quality checklists",
                                "Performance monitoring dashboards",
                                "Customer satisfaction tracking",
                                "Continuous improvement workflows"
                            ]
                        },
                        {
                            name: "Knowledge Management Platform",
                            description: "Centralized knowledge base with best practices, training materials, and expert knowledge sharing for consistent service delivery.",
                            deliverables: [
                                "Centralized knowledge repository",
                                "Training and certification modules",
                                "Expert knowledge sharing",
                                "Best practices documentation"
                            ]
                        },
                        {
                            name: "Analytics and Reporting Suite",
                            description: "Comprehensive analytics platform providing insights into service performance, customer satisfaction, and business growth opportunities.",
                            deliverables: [
                                "Service performance analytics",
                                "Customer satisfaction metrics",
                                "Revenue and profitability analysis",
                                "Growth opportunity identification"
                            ]
                        }
                    ],
                    process: [
                        {
                            name: "Service Process Analysis",
                            percent: 20,
                            color: theme.palette.background.default
                        },
                        {
                            name: "Digital Transformation Strategy",
                            percent: 50,
                            color: theme.palette.secondary.main
                        },
                        {
                            name: "Platform Development and Integration",
                            percent: 70,
                            color: theme.palette.warning.main
                        },
                        {
                            name: "Service Quality Validation",
                            percent: 50,
                            color: theme.palette.warning.light
                        },
                        {
                            name: "Scaling and Continuous Improvement",
                            percent: 30,
                            color: theme.palette.background.default
                        }
                    ]
                });
                break;
            case "rural":
                setIndustryIndex(5);
                setIndustries({
                    image: rural,
                    name: "Rural & Social Enterprises",
                    description: "Create digital platforms that drive social impact and rural development. We work with NGOs, social enterprises, and development organizations to build technology solutions that create access, inclusion, and opportunity.",
                    keyChallenges: [
                        {
                            name: "Limited Digital Infrastructure",
                        },
                        {
                            name: "Low Digital Literacy Levels",
                        },
                        {
                            name: "Language and Accessibility Barriers",
                        },
                        {
                            name: "Funding and Sustainability Challenges"
                        },
                        {
                            name: "Impact Measurement and Reporting"
                        },
                        {
                            name: "Community Engagement and Adoption"
                        }
                    ],
                    solutions: [
                        {
                            name: "Rural Development Platform",
                            description: "Comprehensive platform for rural development programs with beneficiary management, program tracking, and impact measurement capabilities.",
                            deliverables: [
                                "Beneficiary registration and management",
                                "Program implementation tracking",
                                "Impact measurement tools",
                                "Community feedback systems"
                            ]
                        },
                        {
                            name: "Digital Skilling Platform",
                            description: "Mobile-first learning platform designed for rural communities with offline capabilities, vernacular language support, and skill certification.",
                            deliverables: [
                                "Offline learning modules",
                                "Vernacular language support",
                                "Skill assessment and certification",
                                "Progress tracking and analytics"
                            ]
                        },
                        {
                            name: "Microfinance Management System",
                            description: "Digital platform for microfinance institutions with loan management, repayment tracking, and financial literacy modules.",
                            deliverables: [
                                "Loan application and processing",
                                "Repayment tracking and reminders",
                                "Financial literacy content",
                                "Risk assessment tools"
                            ]
                        },
                        {
                            name: "Community Commerce Platform",
                            description: "Marketplace platform connecting rural producers with urban markets, enabling direct sales and fair pricing for rural communities.",
                            deliverables: [
                                "Producer-to-market connectivity",
                                "Product listing and promotion",
                                "Payment and logistics integration",
                                "Quality assurance mechanisms"
                            ]
                        },
                        {
                            name: "Healthcare Access Platform",
                            description: "Telemedicine and health information platform designed for rural areas with offline sync and basic health monitoring capabilities.",
                            deliverables: [
                                "Telemedicine consultations",
                                "Health record management",
                                "Appointment scheduling",
                                "Health awareness content"
                            ]
                        },
                        {
                            name: "Impact Monitoring System",
                            description: "Data collection and analysis platform for measuring social impact with mobile data collection, analytics, and donor reporting.",
                            deliverables: [
                                "Mobile data collection tools",
                                "Impact analytics dashboard",
                                "Donor reporting automation",
                                "Beneficiary feedback system"
                            ]
                        }
                    ],
                    process: [
                        {
                            name: "Community Needs Assessment",
                            percent: 20,
                            color: theme.palette.background.default
                        },
                        {
                            name: "Inclusive Design and Development",
                            percent: 50,
                            color: theme.palette.secondary.main
                        },
                        {
                            name: "Technology Implementation",
                            percent: 70,
                            color: theme.palette.warning.main
                        },
                        {
                            name: "Community Training and Adoption",
                            percent: 50,
                            color: theme.palette.warning.light
                        },
                        {
                            name: "Impact Measurement and Scaling",
                            percent: 30,
                            color: theme.palette.background.default
                        }
                    ]
                });
                break;
            case "public-sector":
                setIndustryIndex(6);
                setIndustries({
                    image: development,
                    name: "Public Sector & Development Programs",
                    description: "Support public-private partnerships and government programs with digital platforms that enhance transparency, efficiency, and citizen engagement. We build solutions that drive governance excellence and development impact.",
                    keyChallenges: [
                        {
                            name: "Complex Regulatory Requirements",
                        },
                        {
                            name: "Legacy System Integration",
                        },
                        {
                            name: "Data Security and Privacy Concerns",
                        },
                        {
                            name: "Citizen Engagement and Accessibility"
                        },
                        {
                            name: "Transparency and Accountability"
                        },
                        {
                            name: "Interoperability Between Departments"
                        }
                    ],
                    solutions: [
                        {
                            name: "Citizen Services Portal",
                            description: "Unified digital platform for citizens to access government services, submit applications, and track status with transparent processing workflows.",
                            deliverables: [
                                "Unified service access portal",
                                "Application tracking system",
                                "Document management",
                                "Multi-language support"
                            ]
                        },
                        {
                            name: "Government Data Management System",
                            description: "Secure data management platform with advanced analytics, reporting capabilities, and compliance with government data protection standards.",
                            deliverables: [
                                "Secure data storage and management",
                                "Advanced analytics and reporting",
                                "Compliance monitoring tools",
                                "Data governance frameworks"
                            ]
                        },
                        {
                            name: "Public Program Management Platform",
                            description: "Comprehensive platform for managing public development programs with beneficiary tracking, fund allocation, and impact monitoring.",
                            deliverables: [
                                "Beneficiary registration and tracking",
                                "Fund allocation and monitoring",
                                "Program performance analytics",
                                "Transparency and audit trails"
                            ]
                        },
                        {
                            name: "Digital Identity and Authentication",
                            description: "Secure digital identity platform with biometric authentication, document verification, and integration with existing ID systems.",
                            deliverables: [
                                "Biometric authentication system",
                                "Document verification tools",
                                "Identity management platform",
                                "API integration capabilities"
                            ]
                        },
                        {
                            name: "Procurement and Contract Management",
                            description: "Digital procurement platform with transparent bidding processes, contract management, and vendor evaluation systems.",
                            deliverables: [
                                "Electronic bidding system",
                                "Contract lifecycle management",
                                "Vendor evaluation and rating",
                                "Audit and compliance reporting"
                            ]
                        },
                        {
                            name: "Public Engagement Platform",
                            description: "Digital platform for citizen engagement with feedback mechanisms, public consultations, and community participation tools.",
                            deliverables: [
                                "Citizen feedback systems",
                                "Public consultation platforms",
                                "Community engagement tools",
                                "Sentiment analysis and reporting"
                            ]
                        }
                    ],
                    process: [
                        {
                            name: "Governance Requirements Analysis",
                            percent: 20,
                            color: theme.palette.background.default
                        },
                        {
                            name: "Compliance and Security Design",
                            percent: 50,
                            color: theme.palette.secondary.main
                        },
                        {
                            name: "Platform Development and Testing",
                            percent: 70,
                            color: theme.palette.warning.main
                        },
                        {
                            name: "Security Audit and Validation",
                            percent: 50,
                            color: theme.palette.warning.light
                        },
                        {
                            name: "Deployment and Citizen Onboarding",
                            percent: 30,
                            color: theme.palette.background.default
                        }
                    ]
                });
                break;
            default: 
                console.log(industryIndex)
                setIndustryIndex(0);
                setIndustries({
                    image: development,
                    name: "Manufacturing & Processing",
                    description: "The manufacturing and processing industry is the backbone of the economy, producing goods that are essential for daily life. M5 Info Solutions provides innovative software solutions to streamline operations, enhance productivity, and ensure quality control in manufacturing processes.",
                    keyChallenges: [
                        {
                            name: "Manual and Paper-based Processes",
                        },
                        {
                            name: "Inefficient Supply Chain Management",
                        },
                        {
                            name: "Poor Data Management",
                        },
                        {
                            name: "Lack of real-time data visibility"
                        },
                        {
                            name: "Difficulty in managing inventory and resources"
                        },
                        {
                            name: "Compliance with industry regulations"
                        }
                    ],
                    solutions: [
                        {
                            name: "Custom ERP for Manufacturing",
                            description: "A custom ERP solution designed specifically for the manufacturing industry, integrating all aspects of production, supply chain, and inventory management.",
                            deliverables: [
                                "Streamlined production processes",
                                "Real-time inventory tracking",
                                "Automated supply chain management",
                                "Enhanced data analytics and reporting"
                            ]
                        },
                        {
                            name: "IoT Integration for Smart Manufacturing",
                            description: "Integration of IoT devices to monitor and control manufacturing processes in real-time, improving efficiency and reducing downtime.",
                            deliverables: [
                                "Real-time monitoring of equipment",
                                "Predictive maintenance alerts",
                                "Data-driven decision making",
                                "Improved operational efficiency"
                            ]
                        },
                        {
                            name: "Quality Control Systems",
                            description: "Automated quality control systems to ensure products meet industry standards and reduce defects.",
                            deliverables: [
                                "Automated quality checks",
                                "Data analytics for quality improvement",
                                "Compliance with industry standards",
                                "Reduced defect rates"
                            ]
                        },
                        {
                            name: "Supply Chain Optimization",
                            description: "Solutions to optimize supply chain management, from procurement to distribution, ensuring timely delivery and cost efficiency.",
                            deliverables: [
                                "Automated procurement processes",
                                "Real-time supply chain visibility",
                                "Cost reduction strategies",
                                "Improved supplier management"
                            ]
                        },
                        {
                            name: "Data Analytics and Reporting",
                            description: "Advanced data analytics tools to provide insights into manufacturing processes, helping businesses make informed decisions.",
                            deliverables: [
                                "Custom dashboards for real-time insights",
                                "Automated reporting",
                                "Data visualization tools",
                                "Enhanced decision-making capabilities"
                            ]
                        },
                        {
                            name: "Training and Support",
                            description: "Comprehensive training and support services to ensure successful implementation and adoption of software solutions.",
                            deliverables: [
                                "On-site and remote training sessions",
                                "24/7 technical support",
                                "User manuals and documentation",
                                "Ongoing software updates and maintenance"
                            ]
                        }
                    ],
                    process: [
                        {
                            name: "Industry Analysis and Discovery",
                            percent: 20,
                            color: theme.palette.background.default
                        },
                        {
                            name: "Solution Design and Prototyping",
                            percent: 50,
                            color: theme.palette.secondary.main
                        },
                        {
                            name: "Development and Implementation",
                            percent: 70,
                            color: theme.palette.warning.main
                        },
                        {
                            name: "Testing and Quality Assurance",
                            percent: 50,
                            color: theme.palette.warning.light
                        },
                        {
                            name: "Deployment and Support",
                            percent: 30,
                            color: theme.palette.background.default
                        }
                    ]
                });
            break;  
        }
        // eslint-disable-next-line
    }, [id])

    return (
        <Box style={{
            maxWidth: "100vw",
            overflowX: "hidden"
        }}>
            <Box style={{
                width: "100vw",
                height: isMobile ? "40vh" : "50vh",
                position: "relative",
            }}>
                <img src={industries.image} alt="industry" style={{
                    width: "100%",
                    height: isMobile ? "40vh" : "50vh",
                    objectFit: "cover",
                    objectPosition: "center",
                }}/>
                <Box style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    top: 0,
                    left: 0,
                    background: theme.palette.common.black,
                    opacity: 0.75,
                    zIndex: 0
                }}/>
                <Box style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    top: isMobile ? "0.5vh" : "1vw",
                    left: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 2,
                    padding: isMobile ? "0 4vw" : "0"
                }}>
                    <Typography variant={isMobile ? "h4" : isTablet ? "h3" : "h1"} style={{
                        color: theme.palette.common.white,
                        textAlign: "center",
                    }}>
                        {industries.name}
                    </Typography>
                </Box>
            </Box>
            <Grid container spacing={4} style={{
                padding: isMobile ? "8vw 4vw" : isTablet ? "6vw 6vw" : "5vw 10vw",
                display: "flex",
                alignItems: "center",
            }}>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
                    <Typography variant="h2" style={{ 
                        fontWeight: 800,
                        color: theme.palette.secondary.main,
                        lineHeight: 1,
                        marginBottom: isMobile ? "20px" : "30px",
                    }}>
                        Key challeges we solve
                    </Typography>
                    <Typography variant="body1" style={{
                        lineHeight: isMobile && "1.5" 
                    }}>
                        {industries.description}
                    </Typography>
                </Grid>
                <Grid size={{xs: 12, sm: 12, md: 6, lg: 6 }}>
                    <Grid container spacing={2}>
                        {industries.keyChallenges?.map((challenge, index) => (
                            <Grid size={{xs: 12, sm: 6, md: 6, lg: 6}} key={index} style={{
                                display: "flex",
                                alignItems: "stretch",
                                justifyContent: "stretch",
                            }}>
                                <Box style={{
                                    width: "100%",
                                    padding: isMobile ? "15px" : "20px",
                                    backgroundColor: theme.palette.warning.main,
                                    borderRadius: "3px"
                                }}>
                                    <Typography variant="h6">
                                        {challenge.name}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            </Grid>
            <Box style={{
                paddingBottom: isMobile ? "8vw" : "5vw",
                paddingTop: isMobile ? "6vw" : "3vw",
                padding: isMobile ? "6vw 4vw 8vw 4vw" : isTablet ? "3vw 6vw 5vw 6vw" : "3vw 0 5vw 0"
            }}>
                <Typography variant="h2" style={{ 
                    fontWeight: 800,
                    color: theme.palette.secondary.main,
                    lineHeight: 1,
                    marginBottom: isMobile ? "6vw" : "5vw",
                    textAlign: "center",
                }}>
                    Our Process
                </Typography>
                <Box style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'stretch',
                    width: "100%",
                    marginBottom: "20px",
                    flexDirection: isMobile || isTablet ? "column" : "row",
                    gap: isMobile || isTablet ? "15px" : 0
                }}>
                    {industries.process?.map((step, index) => (
                        <Box style={{
                            width: isMobile || isTablet ? "90%" : "30%",
                            height: isMobile || isTablet ? "120px" : "150px",
                            backgroundColor: step.color,
                            clipPath: isMobile || isTablet ? "none" : "polygon(75% 0%, 100% 50%, 75% 100%, 0% 100%, 25% 50%, 0% 0%)",
                            borderRadius: isMobile || isTablet ? "8px" : "5px",
                            marginLeft: (index === 0 || isMobile || isTablet) ? "0" : "-10%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                        }} key={index}>
                            <Typography variant="h6" style={{ 
                                fontWeight: 700, 
                                padding: isMobile || isTablet ? "0 15px" : "0 30%",
                                textAlign: "center"
                            }}>
                                {step.name}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
            <Grid container spacing={4} style={{
                padding: isMobile ? "8vw 4vw" : isTablet ? "6vw 6vw" : "5vw 8vw",
                background: theme.palette.primary.main,
                display: "flex",
                alignItems: "stretch",
                justifyContent: "center",
            }}>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                    <Typography variant={isMobile ? "h4" : "h2"} style={{ 
                        textAlign: "center",
                        color: theme.palette.common.white,
                        marginBottom: isMobile ? "4vw" : isTablet ? "3vw" : "2vw"
                    }}>
                        Solutions we offer
                    </Typography>
                </Grid>
                {industries.solutions?.map((solution, index) => (
                    <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6 }} key={index} style={{
                        color: theme.palette.common.white,
                        padding: isMobile ? "20px" : "30px",
                        boxShadow: "rgba(0, 0, 0, 0.16) 0px 10px 36px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px",
                        borderLeft: `5px solid ${theme.palette.secondary.main}`,
                    }}>
                        <Typography variant="h5" style={{
                            fontWeight: 700,
                        }}>
                            {solution.name}
                        </Typography>
                        <Typography variant="body2" style={{
                            lineHeight: isMobile ? "1.5" : undefined,
                            marginBottom: "20px"
                        }}>
                            {solution.description}
                        </Typography>
                        <ul style={{padding: isMobile ? "5px" : "10px"}}>
                            {solution.deliverables.map((deliverable, i) => (
                                <li key={i} style={{padding: 0, marginBottom: "10px"}}>
                                    <Typography variant="body1">
                                        {deliverable}
                                    </Typography>
                                </li>
                            ))}
                        </ul>
                    </Grid>
                ))}
            </Grid>
        </Box>
    )
}

export default Industries;